import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { AlertTriangle, ImageIcon, KeyRound, Loader2, Save, Trash2, Upload } from 'lucide-react'
import { normalizePolicy } from '../../../services/mdmApi'
import { useMdmBrandingLogo, useUploadMdmBrandingLogo } from '../../../hooks/useMdm'
import { errorMessage } from './mobileUtils'

const RESTRICTIONS = [
  { key: 'disableFactoryReset', label: 'Bloquear restauração de fábrica' },
  { key: 'disableSafeBoot', label: 'Bloquear modo seguro' },
  { key: 'disableUsbFileTransfer', label: 'Bloquear transferência de arquivos por USB' },
  { key: 'disableInstallUnknownSources', label: 'Bloquear instalação de fontes desconhecidas' },
  { key: 'disableUninstallApps', label: 'Bloquear desinstalação de apps' },
  { key: 'disableAddUser', label: 'Bloquear novos usuários' },
  { key: 'disableLocationOff', label: 'Impedir desligar a localização' },
  { key: 'disableCamera', label: 'Desativar câmera' },
  { key: 'disableScreenCapture', label: 'Bloquear print de tela (no tablet)' },
  { key: 'disableStatusBar', label: 'Ocultar/travar barra de status' },
  { key: 'disableConfigWifi', label: 'Bloquear configuração de Wi-Fi' },
  { key: 'disableBluetooth', label: 'Desativar Bluetooth' },
  { key: 'disableAdjustVolume', label: 'Bloquear ajuste de volume' },
]

const QUICK_PANEL = [
  { key: 'wifi', label: 'Wi-Fi' },
  { key: 'rotation', label: 'Girar a tela' },
  { key: 'brightness', label: 'Brilho' },
  { key: 'volume', label: 'Volume' },
  { key: 'support', label: 'Chamar suporte' },
]

const CONSENT_OPTIONS = [
  {
    value: 'ask',
    label: 'Pedir permissão à pessoa no tablet',
    hint: 'A pessoa no tablet precisa tocar em "Permitir" (tem 30 s) antes de a tela aparecer no painel.',
  },
  {
    value: 'notify',
    label: 'Só avisar (recomendado)',
    hint: 'A tela aparece direto e o tablet mostra a faixa vermelha "Suporte vendo a tela", com o botão Encerrar.',
  },
  {
    value: 'silent',
    label: 'Sem aviso nenhum',
    hint: 'Nada aparece no tablet. Use só em tablets sem ninguém por perto (totens, painéis).',
  },
]

const VPN_OPTIONS = [
  {
    value: 'legacy',
    label: 'Como está hoje (tablets que já têm VPN mantêm; novos ficam sem)',
    hint: 'Nada muda nos tablets atuais. Tablets cadastrados daqui para frente ficam sem VPN.',
  },
  { value: 'on', label: 'Ligada', hint: 'Cada tablet ganha um acesso na rede VPN padrão e liga a VPN sozinho.' },
  { value: 'off', label: 'Desligada', hint: 'Os tablets ficam sem VPN. O endereço reservado não muda se a VPN voltar a ser ligada.' },
]

const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const LOGO_MAX_BYTES = 512 * 1024
const PIN_RE = /^\d{4,8}$/

const toLines = (arr) => (arr || []).join('\n')
const fromLines = (text) => String(text || '').split(/[\n,]/).map((s) => s.trim()).filter(Boolean)
const textOrNull = (v) => (v == null || String(v).trim() === '' ? null : String(v).trim())
/** Número do campo; vazio vira NaN (reprova na validação em vez de virar 0). */
const toNum = (v) => (v == null || String(v).trim() === '' ? NaN : Number(v))
const intIn = (v, min, max) => {
  const n = Number(v)
  return Number.isInteger(n) && n >= min && n <= max
}

const PIN_IDLE = { mode: 'keep', value: '', saved: false, prev: null }

/**
 * Formulário da política MDM (CONTRACT.md §5), usado no tablet (aba Políticas) e no grupo/raiz.
 * `policy` é o ponto de partida; recarrega quando `reloadKey` muda e não há edição pendente.
 * Campos que o formulário não conhece voltam intactos no salvar.
 * `onSave(body)` resolve com a mensagem de sucesso (ou lança o erro).
 */
export default function PolicyForm({ policy: initial, reloadKey, readOnly, saving, onSave, saveLabel = 'Salvar e aplicar', toolbar, singleColumn }) {
  const [policy, setPolicy] = useState(() => normalizePolicy(initial))
  const [dirty, setDirty] = useState(false)
  const [msg, setMsg] = useState(null)
  // Listas editadas como texto livre (um pacote por linha).
  const [kioskText, setKioskText] = useState(() => toLines(policy.kiosk.packages))
  const [blockedText, setBlockedText] = useState(() => toLines(policy.apps.blocked))
  const [hiddenText, setHiddenText] = useState(() => toLines(policy.apps.hidden))
  // PIN fixo: o front nunca manda hash — manda `localAdmin.pin` (novo) ou `pinHash: null` (remover).
  const [pin, setPin] = useState(PIN_IDLE)

  useEffect(() => {
    if (dirty) return
    const p = normalizePolicy(initial)
    setPolicy(p)
    setKioskText(toLines(p.kiosk.packages))
    setBlockedText(toLines(p.apps.blocked))
    setHiddenText(toLines(p.apps.hidden))
    setPin(PIN_IDLE)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey])

  const update = (section, key, value) => {
    setDirty(true)
    setPolicy((prev) => (key == null
      ? { ...prev, [section]: value }
      : { ...prev, [section]: { ...prev[section], [key]: value } }))
  }

  const updateQuickPanel = (key, value) => {
    setDirty(true)
    setPolicy((prev) => ({
      ...prev,
      kioskUi: { ...prev.kioskUi, quickPanel: { ...prev.kioskUi.quickPanel, [key]: value } },
    }))
  }

  const setVpnMode = (mode) => {
    setDirty(true)
    setPolicy((prev) => {
      const { enabled: _enabled, ...rest } = prev.vpn
      return { ...prev, vpn: mode === 'legacy' ? rest : { ...rest, enabled: mode === 'on' } }
    })
  }

  const changePin = (next) => {
    setDirty(true)
    setPin(next)
  }

  const buildLocalAdmin = () => {
    const { pin: _stray, ...la } = policy.localAdmin
    const out = {
      ...la,
      gestureTaps: toNum(la.gestureTaps),
      maxExitMinutes: toNum(la.maxExitMinutes),
      idleRelockSeconds: toNum(la.idleRelockSeconds),
      maxPauseMinutes: toNum(la.maxPauseMinutes),
    }
    if (pin.mode === 'set') {
      delete out.pinHash
      out.pin = pin.value
    } else if (pin.mode === 'remove') {
      out.pinHash = null
    }
    // mode 'keep': pinHash segue exatamente como veio do servidor (ou ausente).
    return out
  }

  const validate = (body) => {
    if (body.kiosk.enabled && body.kiosk.packages.length === 0) return 'Informe ao menos um pacote para o modo quiosque.'
    const la = body.localAdmin
    if (pin.mode === 'set' && !PIN_RE.test(pin.value)) return 'O PIN do técnico precisa ter de 4 a 8 dígitos (só números).'
    if (!intIn(la.gestureTaps, 3, 10)) return 'Toques no logotipo: use um número de 3 a 10.'
    if (!intIn(la.maxExitMinutes, 15, 240)) return 'Tempo máximo fora do quiosque: use de 15 a 240 minutos.'
    if (!(la.idleRelockSeconds === 0 || intIn(la.idleRelockSeconds, 60, 900))) {
      return 'Volta ao quiosque por inatividade: use 0 (desligado) ou de 60 a 900 segundos.'
    }
    if (la.allowPause && !intIn(la.maxPauseMinutes, 30, 1440)) return 'Pausa máxima do MDM: use de 30 a 1440 minutos.'
    return null
  }

  const handleSave = async () => {
    setMsg(null)
    const localAdmin = buildLocalAdmin()
    if (!localAdmin.allowPause && !intIn(localAdmin.maxPauseMinutes, 30, 1440)) localAdmin.maxPauseMinutes = 480
    const body = {
      ...policy,
      kiosk: { ...policy.kiosk, packages: fromLines(kioskText) },
      apps: { ...policy.apps, blocked: fromLines(blockedText), hidden: fromLines(hiddenText) },
      password: { ...policy.password, minLength: Number(policy.password.minLength) || 0 },
      location: { ...policy.location, intervalSeconds: Math.max(10, Number(policy.location.intervalSeconds) || 60) },
      localAdmin,
      branding: {
        ...policy.branding,
        orgName: textOrNull(policy.branding.orgName),
        supportPhone: textOrNull(policy.branding.supportPhone),
        supportHours: textOrNull(policy.branding.supportHours),
      },
    }
    const invalid = validate(body)
    if (invalid) {
      setMsg({ ok: false, text: invalid })
      return
    }
    try {
      const text = await onSave(body)
      setDirty(false)
      setMsg({ ok: true, text })
      if (pin.mode === 'remove') {
        setPolicy((prev) => ({ ...prev, localAdmin: { ...prev.localAdmin, pinHash: null } }))
        setPin(PIN_IDLE)
      } else if (pin.mode === 'set') {
        // Até a política recarregar do servidor, um novo salvar reenvia o mesmo PIN (o servidor refaz o hash).
        setPin((prev) => ({ ...prev, saved: true }))
      }
    } catch (e) {
      setMsg({ ok: false, text: errorMessage(e, 'Falha ao salvar a política.') })
    }
  }

  const la = policy.localAdmin
  const vpnMode = policy.vpn.enabled === true ? 'on' : policy.vpn.enabled === false ? 'off' : 'legacy'
  const consent = CONSENT_OPTIONS.find((o) => o.value === la.remoteConsent) || CONSENT_OPTIONS[1]

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {toolbar}
        <div className="flex-1" />
        {!readOnly && (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-theme-primary text-white rounded-lg hover:bg-theme-primary/90 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saveLabel}
          </button>
        )}
      </div>
      {msg && <p className={clsx('text-sm', msg.ok ? 'text-green-700' : 'text-red-600')}>{msg.text}</p>}

      <fieldset disabled={readOnly} className={clsx('grid gap-4', !singleColumn && 'lg:grid-cols-2')}>
        <div className="space-y-4">
          <Card title="Restrições">
            <div className="space-y-1.5">
              {RESTRICTIONS.map((r) => (
                <Check key={r.key} label={r.label} checked={!!policy.restrictions[r.key]} onChange={(v) => update('restrictions', r.key, v)} />
              ))}
            </div>
          </Card>

          <Card title="Acesso do técnico no tablet">
            <p className="text-xs text-gray-500 mb-3">
              O técnico abre o modo técnico tocando {String(la.gestureTaps || '')} vezes seguidas no logotipo da barra do quiosque
              {la.volumeKeys ? ' ou apertando Vol+ Vol+ Vol− Vol−' : ''}. O código de uso único gerado no painel
              (botão &quot;Código de acesso&quot; na tela do tablet) sempre funciona, com ou sem PIN.
            </p>

            <SubTitle>PIN fixo do grupo</SubTitle>
            <PinEditor
              pin={pin}
              hasHash={!!la.pinHash}
              onChange={changePin}
            />
            <p className="text-xs text-gray-500 mt-1">
              Um PIN que os técnicos conhecem, para não precisar ligar para o suporte. Serve só para o modo técnico — nunca para remover o MDM.
            </p>

            <Divider />
            <SubTitle>Como abrir no tablet</SubTitle>
            <NumberField label="Toques no logotipo" min={3} max={10} value={la.gestureTaps}
              onChange={(v) => update('localAdmin', 'gestureTaps', v)} suffix="toques (3 a 10)" />
            <Check label="Também pelos botões de volume (Vol+ Vol+ Vol− Vol−)" checked={la.volumeKeys} onChange={(v) => update('localAdmin', 'volumeKeys', v)} />

            <Divider />
            <SubTitle>O que o técnico pode fazer</SubTitle>
            <div className="space-y-2">
              <NumberField label="Sair do quiosque por no máximo" min={15} max={240} value={la.maxExitMinutes}
                onChange={(v) => update('localAdmin', 'maxExitMinutes', v)} suffix="min (15 a 240)" />
              <NumberField label="Voltar ao quiosque depois de" min={0} max={900} value={la.idleRelockSeconds}
                onChange={(v) => update('localAdmin', 'idleRelockSeconds', v)} suffix="s sem toque (60 a 900; 0 = não volta por isso)" />
              <Check label="Permitir pausar o MDM (tira quiosque e restrições por um tempo)" checked={la.allowPause} onChange={(v) => update('localAdmin', 'allowPause', v)} />
              {la.allowPause && (
                <div className="pl-6">
                  <NumberField label="Pausa de no máximo" min={30} max={1440} value={la.maxPauseMinutes}
                    onChange={(v) => update('localAdmin', 'maxPauseMinutes', v)} suffix="min (30 a 1440)" />
                </div>
              )}
              <Check label="Exigir a acessibilidade do agente ligada (o tablet mostra uma tela até alguém ligar)"
                checked={la.requireAccessibility !== false} onChange={(v) => update('localAdmin', 'requireAccessibility', v)} />
              <Check label="Permitir remover o MDM pelo tablet" checked={la.allowRemove} onChange={(v) => update('localAdmin', 'allowRemove', v)} />
              {la.allowRemove && (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-red-50 border border-red-200 text-xs text-red-800">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    Com isto ligado, um técnico no local consegue tirar o tablet do gerenciamento usando um código de remoção
                    gerado aqui no painel (&quot;Código de acesso&quot; → &quot;Remover o MDM&quot;, com o código do autenticador).
                    Depois disso o tablet fica livre: para voltar a gerenciar é preciso cadastrá-lo de novo pelo cabo USB.
                  </span>
                </div>
              )}
            </div>

            <Divider />
            <SubTitle>Quando o suporte abre a tela remota</SubTitle>
            <select value={consent.value} onChange={(e) => update('localAdmin', 'remoteConsent', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
              {CONSENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <p className="text-xs text-gray-500 mt-1">{consent.hint}</p>
          </Card>
        </div>

        <div className="space-y-4">
          <Card title="Modo quiosque">
            <Check label="Ativar quiosque (trava o tablet nos apps abaixo)" checked={policy.kiosk.enabled} onChange={(v) => update('kiosk', 'enabled', v)} />
            <label className="block text-xs text-gray-600 mt-2 mb-1">Pacotes permitidos (um por linha — o primeiro abre ao iniciar)</label>
            <textarea rows={3} value={kioskText} onChange={(e) => { setKioskText(e.target.value); setDirty(true) }}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-mono" placeholder="com.empresa.app" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 mt-2">
              <Check label="Botão início" checked={policy.kiosk.showHome} onChange={(v) => update('kiosk', 'showHome', v)} />
              <Check label="Notificações" checked={policy.kiosk.showNotifications} onChange={(v) => update('kiosk', 'showNotifications', v)} />
              <Check label="Info do sistema" checked={policy.kiosk.showSystemInfo} onChange={(v) => update('kiosk', 'showSystemInfo', v)} />
            </div>
          </Card>

          <Card title="Barra do quiosque">
            <Check label="Mostrar a barra no topo da tela (com o logotipo)" checked={policy.kioskUi.statusBar}
              onChange={(v) => update('kioskUi', 'statusBar', v)} />
            <label className="block text-xs text-gray-600 mt-2 mb-1">O que aparece no painel rápido</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
              {QUICK_PANEL.map((q) => (
                <Check key={q.key} label={q.label} checked={policy.kioskUi.quickPanel[q.key]} onChange={(v) => updateQuickPanel(q.key, v)} />
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Wi-Fi e Volume também obedecem às restrições &quot;Bloquear configuração de Wi-Fi&quot; e &quot;Bloquear ajuste de volume&quot;.
            </p>
            {!policy.kioskUi.statusBar && !la.volumeKeys && (
              <p className="flex items-start gap-1.5 text-xs text-amber-800 mt-2">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                Sem a barra e sem os botões de volume, o técnico não tem como abrir o modo técnico no tablet.
              </p>
            )}
          </Card>

          <Card title="Senha de tela">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <select value={policy.password.quality} onChange={(e) => update('password', 'quality', e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5">
                <option value="none">Sem exigência</option>
                <option value="pin">PIN numérico</option>
                <option value="alphanumeric">Alfanumérica</option>
              </select>
              {policy.password.quality !== 'none' && (
                <label className="flex items-center gap-2">
                  Tamanho mínimo
                  <input type="number" min={4} max={16} value={policy.password.minLength}
                    onChange={(e) => update('password', 'minLength', e.target.value)}
                    className="w-20 border border-gray-300 rounded-lg px-2 py-1.5" />
                </label>
              )}
            </div>
          </Card>

          <Card title="VPN e localização">
            <label className="block text-xs text-gray-600 mb-1">VPN no tablet</label>
            <select value={vpnMode} onChange={(e) => setVpnMode(e.target.value)} className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
              {VPN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <p className="text-xs text-gray-500 mt-1 mb-2">{VPN_OPTIONS.find((o) => o.value === vpnMode)?.hint}</p>
            <Check label="VPN sempre ligada" checked={policy.vpn.alwaysOn} disabled={vpnMode === 'off'} onChange={(v) => update('vpn', 'alwaysOn', v)} />
            <Check label="Bloquear internet fora da VPN (lockdown)" checked={policy.vpn.lockdown} disabled={vpnMode === 'off' || !policy.vpn.alwaysOn} onChange={(v) => update('vpn', 'lockdown', v)} />
            <div className="border-t border-gray-100 my-2" />
            <Check label="Rastrear localização (GPS)" checked={policy.location.enabled} onChange={(v) => update('location', 'enabled', v)} />
            {policy.location.enabled && (
              <label className="flex items-center gap-2 text-sm mt-1">
                Intervalo (s)
                <input type="number" min={10} value={policy.location.intervalSeconds}
                  onChange={(e) => update('location', 'intervalSeconds', e.target.value)}
                  className="w-24 border border-gray-300 rounded-lg px-2 py-1.5" />
              </label>
            )}
            <div className="border-t border-gray-100 my-2" />
            <Check label="Manter tela ligada enquanto carrega" checked={policy.keepScreenOnWhileCharging} onChange={(v) => update('keepScreenOnWhileCharging', null, v)} />
          </Card>

          <Card title="Apps bloqueados / ocultos">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Bloqueados (suspensos)</label>
                <textarea rows={3} value={blockedText} onChange={(e) => { setBlockedText(e.target.value); setDirty(true) }}
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-mono" placeholder="com.android.chrome" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Ocultos</label>
                <textarea rows={3} value={hiddenText} onChange={(e) => { setHiddenText(e.target.value); setDirty(true) }}
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-mono" placeholder="com.android.vending" />
              </div>
            </div>
          </Card>

          <Card title="Marca">
            <BrandingLogo
              fileId={policy.branding.logoFileId}
              onChange={(logoFileId, logoSha256) => {
                setDirty(true)
                setPolicy((prev) => ({ ...prev, branding: { ...prev.branding, logoFileId, logoSha256 } }))
              }}
            />
            <div className="space-y-2 mt-3">
              <TextField label="Nome da organização" value={policy.branding.orgName} placeholder="ex.: Secretaria de Saúde"
                onChange={(v) => update('branding', 'orgName', v)} />
              <TextField label="Telefone do suporte" value={policy.branding.supportPhone} placeholder="ex.: (21) 99999-0000"
                onChange={(v) => update('branding', 'supportPhone', v)} />
              <TextField label="Horário do suporte" value={policy.branding.supportHours} placeholder="ex.: seg a sex, 8h às 18h"
                onChange={(v) => update('branding', 'supportHours', v)} />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              A logo aparece na barra do quiosque e nas telas do agente; nome, telefone e horário aparecem em &quot;Chamar suporte&quot;.
              A marca Automais continua no rodapé.
            </p>
          </Card>
        </div>
      </fieldset>
    </div>
  )
}

/** PIN fixo: mostra "PIN definido" (Trocar/Remover), o campo do PIN novo ou a remoção pendente. */
function PinEditor({ pin, hasHash, onChange }) {
  const defined = (pin.mode === 'keep' && hasHash) || (pin.mode === 'set' && pin.saved)

  if (pin.mode === 'remove') {
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-amber-800">O PIN fixo será removido ao salvar.</span>
        <button type="button" onClick={() => onChange(pin.prev || PIN_IDLE)} className="text-xs text-theme-primary hover:underline">
          Desfazer
        </button>
      </div>
    )
  }

  if (defined) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
          <KeyRound className="w-3.5 h-3.5" /> PIN definido
        </span>
        <button type="button" onClick={() => onChange({ mode: 'set', value: '', saved: false, prev: pin })}
          className="px-2.5 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50">
          Trocar PIN
        </button>
        <button type="button" onClick={() => onChange({ mode: 'remove', value: '', saved: false, prev: pin })}
          className="px-2.5 py-1 text-xs border border-red-300 text-red-700 rounded-md hover:bg-red-50">
          Remover PIN
        </button>
      </div>
    )
  }

  if (pin.mode === 'set') {
    const valid = PIN_RE.test(pin.value)
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <input
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          maxLength={8}
          value={pin.value}
          onChange={(e) => onChange({ ...pin, value: e.target.value.replace(/\D/g, '').slice(0, 8) })}
          placeholder="4 a 8 dígitos"
          className={clsx('w-36 border rounded-lg px-2 py-1.5 font-mono tracking-widest', pin.value && !valid ? 'border-red-400' : 'border-gray-300')}
        />
        <button type="button" onClick={() => onChange(pin.prev || PIN_IDLE)} className="text-xs text-gray-500 hover:text-gray-800">
          Cancelar
        </button>
        <span className={clsx('text-xs', pin.value && !valid ? 'text-red-600' : 'text-gray-500')}>
          {pin.value && !valid ? 'Use de 4 a 8 números.' : 'Vale depois de salvar.'}
        </span>
      </div>
    )
  }

  return (
    <Check label="Usar um PIN fixo" checked={false} onChange={(v) => v && onChange({ mode: 'set', value: '', saved: false, prev: pin })} />
  )
}

/** Logo do cliente: sobe no tenant (§12.4), mostra a prévia pelo GET e grava fileId + sha256 na política. */
function BrandingLogo({ fileId, onChange }) {
  const inputRef = useRef(null)
  const upload = useUploadMdmBrandingLogo()
  const { data: blob, isLoading, isError } = useMdmBrandingLogo(fileId)
  const [url, setUrl] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!blob) { setUrl(null); return undefined }
    const u = URL.createObjectURL(blob)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [blob])

  const pick = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setError(null)
    if (!LOGO_TYPES.includes(file.type)) {
      setError('Use uma imagem PNG, JPEG ou WebP.')
      return
    }
    if (file.size > LOGO_MAX_BYTES) {
      setError('A imagem passa de 512 KB. Reduza o tamanho e tente de novo.')
      return
    }
    try {
      const res = await upload.mutateAsync(file)
      onChange(res.fileId, res.sha256)
    } catch (err) {
      setError(errorMessage(err, 'Falha ao enviar a logo.'))
    }
  }

  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="w-28 h-16 flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex-shrink-0">
          {fileId && isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          ) : url ? (
            <img src={url} alt="Logo do cliente" className="max-w-full max-h-full object-contain" />
          ) : (
            <ImageIcon className="w-6 h-6 text-gray-300" />
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={inputRef} type="file" accept={LOGO_TYPES.join(',')} className="hidden" onChange={pick} />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={upload.isPending}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
            {upload.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {fileId ? 'Trocar logo' : 'Enviar logo'}
          </button>
          {fileId && (
            <button type="button" onClick={() => onChange(null, null)}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-gray-300 text-gray-600 rounded-md hover:bg-gray-50">
              <Trash2 className="w-3.5 h-3.5" /> Remover logo
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-1">
        PNG, JPEG ou WebP, até 512 KB. {fileId ? 'Vale para os tablets depois de salvar a política.' : 'Sem logo, os tablets mostram só a marca Automais.'}
      </p>
      {fileId && isError && <p className="text-xs text-amber-700 mt-1">Não foi possível carregar a prévia da logo.</p>}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  )
}

function Card({ title, children }) {
  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
      {children}
    </section>
  )
}

function SubTitle({ children }) {
  return <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">{children}</h4>
}

function Divider() {
  return <div className="border-t border-gray-100 my-3" />
}

function Check({ label, checked, onChange, disabled }) {
  return (
    <label className={clsx('flex items-center gap-2 text-sm', disabled ? 'text-gray-400' : 'text-gray-700')}>
      <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  )
}

function NumberField({ label, value, onChange, min, max, suffix }) {
  return (
    <label className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
      {label}
      <input type="number" min={min} max={max} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        className="w-20 border border-gray-300 rounded-lg px-2 py-1.5" />
      {suffix && <span className="text-xs text-gray-500">{suffix}</span>}
    </label>
  )
}

function TextField({ label, value, onChange, placeholder }) {
  return (
    <label className="block text-sm">
      <span className="block text-xs text-gray-600 mb-1">{label}</span>
      <input type="text" value={value ?? ''} placeholder={placeholder} maxLength={120} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
    </label>
  )
}
